/* eslint-disable react/no-unescaped-entities */
import React, {useEffect, useState, useCallback} from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {GoogleLogin} from "@react-oauth/google";
import {UserToken} from "../../utils/User/UserToken";
import {jwtDecode} from "jwt-decode";
import {requestEmailOtpLogin, setTokenInApi, verifyEmailOtp} from "../../utils/User/user-openapi-hooks";
import {loginUser, removeUserFromState, setUserState} from "../../store/navBar/loginSlice";
import {useAppDispatch} from "../../hooks/hooks";
import { Alert } from '@mui/material';
import {deleteRegisterState} from "../../store/navBar/userSlice";
import {resetAvailabilityState} from "../../store/Host/sitesManagement/availabilitySlice";
import {resetTourComponentState} from "../../store/Vanlifer/vanliferComponentsSlice";
import {resetTours} from "../../store/Vanlifer/tourSlice";
import Button from "@mui/material/Button";
import {User} from "../../services/restserver-openapi";


interface RegisterGoogleLoginProps {
    disableNext: (value: boolean) => void;
    handleNext: () => void;
    localUserData: User;
    setLocalUserData: (value: User) => void;
    allowEmailOtp?: boolean;
}

const RegisterGoogleLogin:React.FC<RegisterGoogleLoginProps> = ({ disableNext, handleNext, localUserData, setLocalUserData, allowEmailOtp = false }) => {
    const dispatch = useAppDispatch();
    const [showErrorAccountAlreadyExists, setShowErrorAccountAlreadyExists] = useState(false);
    const [email, setEmail] = useState(localUserData.person?.email ?? '');
    const [otp, setOtp] = useState('');
    const [otpRequested, setOtpRequested] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpMessage, setOtpMessage] = useState('');
    const [isOtpRequestPending, setIsOtpRequestPending] = useState(false);
    const [isOtpVerifyPending, setIsOtpVerifyPending] = useState(false);


    const handleCallbackResponse = async (response: any) => {
        const identityToken: string = response.credential
        const userObject: UserToken = jwtDecode(identityToken);
        setTokenInApi(identityToken);
        dispatch(setUserState({
            userName: userObject.name,
            token: identityToken,
            picture: userObject.picture,
            loginFirstname: userObject.given_name,
            loginLastname: userObject.family_name,
            loginEmail: userObject.email
        }));
        const loginResult = await dispatch(loginUser(identityToken));
        if (loginResult.payload) {
            setShowErrorAccountAlreadyExists(true);
        } else {
            setLocalUserData({
                ...localUserData,
                "person": {
                    ...localUserData.person,
                    "firstname": userObject.given_name,
                    "lastname": userObject.family_name,
                    "email": userObject.email,
                }
            });
            disableNext(false);
            handleNext();
        }
    }

    const handleSignOut = useCallback(() => {
        setShowErrorAccountAlreadyExists(false);
        setOtpError('');
        setOtpMessage('');
        setOtp('');
        setOtpRequested(false);
        dispatch(removeUserFromState());
        dispatch(deleteRegisterState());
        dispatch(resetAvailabilityState());
        dispatch(resetTourComponentState());
        dispatch(resetTours());
        sessionStorage.clear();
    }, [dispatch]);

    useEffect(() => {
        handleSignOut();
        disableNext(true);
    }, [handleSignOut, disableNext]);

    const handleEmailOtpRequest = async () => {
        if (!email.trim()) {
            setOtpError('Bitte gib deine E-Mail-Adresse ein.');
            return;
        }

        try {
            setIsOtpRequestPending(true);
            setOtpError('');
            setOtpMessage('');
            await requestEmailOtpLogin(email.trim());
            setOtpRequested(true);
            setOtpMessage('Wir haben dir einen 6-stelligen Code per E-Mail geschickt.');
        } catch (error) {
            setOtpError('Der Code konnte nicht angefordert werden. Bitte pruefe deine E-Mail-Adresse.');
        } finally {
            setIsOtpRequestPending(false);
        }
    };

    const handleEmailOtpVerify = async () => {
        if (!email.trim()) {
            setOtpError('Bitte gib deine E-Mail-Adresse ein.');
            return;
        }

        if (!/^\d{6}$/.test(otp)) {
            setOtpError('Bitte gib einen 6-stelligen Zahlencode ein.');
            return;
        }

        try {
            setIsOtpVerifyPending(true);
            setOtpError('');
            setShowErrorAccountAlreadyExists(false);
            const tokenAuthorization = await verifyEmailOtp(email.trim(), otp);
            const identityToken = tokenAuthorization.identityToken ?? '';

            setTokenInApi(identityToken);
            dispatch(setUserState({
                userName: email.trim(),
                token: identityToken,
                picture: '',
                loginFirstname: '',
                loginLastname: '',
                loginEmail: email.trim()
            }));

            const loginResult = await dispatch(loginUser(identityToken));
            if (loginResult.payload) {
                setShowErrorAccountAlreadyExists(true);
                return;
            }

            setLocalUserData({
                ...localUserData,
                person: {
                    ...localUserData.person,
                    email: email.trim(),
                }
            });
            disableNext(false);
            handleNext();
        } catch (error) {
            const backendMessage = error instanceof Error ? error.message : '';
            const backendMessageLower = backendMessage.toLowerCase();

            if (backendMessage.includes('NOT_FOUND') || backendMessageLower.includes('not found')) {
                setOtpError('Zu dieser E-Mail-Adresse wurde kein Account gefunden. Bitte registriere dich zuerst oder nutze einen anderen Login.');
            } else if (backendMessage.includes('No OTP was requested')) {
                setOtpError('Es wurde kein gueltiger Code angefordert. Bitte fordere einen neuen Code an.');
            } else if (backendMessage.includes('OTP expired')) {
                setOtpError('Der Code ist abgelaufen. Bitte fordere einen neuen Code an.');
            } else if (backendMessage.includes('Invalid OTP')) {
                setOtpError('Der Code ist ungueltig. Bitte pruefe den 6-stelligen Code oder fordere einen neuen an.');
            } else {
                setOtpError(`OTP-Pruefung fehlgeschlagen: ${backendMessage || 'Unbekannter Fehler'}`);
            }
        } finally {
            setIsOtpVerifyPending(false);
        }
    };

    return (
        <Box>
            <Box marginBottom={1}>
                <GoogleLogin onSuccess={(credentialResponse) => {
                    handleCallbackResponse(credentialResponse)
                }} onError={() => {}}/>
            </Box>
            {allowEmailOtp && (
                <Box marginTop={3}>
                    <Box marginBottom={2}>
                        <Divider>oder</Divider>
                    </Box>
                    <Stack spacing={2}>
                        <TextField
                            label="E-Mail"
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            fullWidth
                        />
                        <Button variant="outlined" onClick={handleEmailOtpRequest} disabled={isOtpRequestPending}>
                            {isOtpRequestPending ? 'Code wird gesendet...' : 'OTP per E-Mail anfordern'}
                        </Button>
                        <TextField
                            label="6-stelliger OTP-Code"
                            value={otp}
                            onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                            disabled={!otpRequested}
                            fullWidth
                        />
                        <Button variant="contained" onClick={handleEmailOtpVerify} disabled={isOtpVerifyPending}>
                            {isOtpVerifyPending ? 'Code wird geprueft...' : 'Mit E-Mail und OTP fortfahren'}
                        </Button>
                        {otpMessage && <Alert severity="success">{otpMessage}</Alert>}
                        {otpError && <Alert severity="error">{otpError}</Alert>}
                    </Stack>
                </Box>
            )}
            {showErrorAccountAlreadyExists &&
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={handleSignOut}>Logout und mit anderem Login erneut versuchen.</Button>}
                >
                    Mit diesem Login existiert bereits ein Account.
                </Alert>
            }
        </Box>

);
};

export default RegisterGoogleLogin;
